import { Dispatch } from 'redux'
import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import { ErrorNotification } from 'app/views/components/notifications/notification'
import {
  getServicing,
  servicingFeeAmountsFailed,
  servicingLoadFailed,
  servicingWindowRequested,
  setServicingFeeAmounts,
  startLoadingServicing,
  stopLoadingServicing,
} from './actions'
import { ServicingState } from './reducer'
import {
  ServicingFeeAmountsResponse,
  ServicingFeedQuery,
  ServicingFeedResponse,
} from './types'

type GetState = () => { servicing: ServicingState }

/** One request at a time per window: a strict-mode double mount, or a page effect firing during a load, joins it. */
const inFlight = new Map<string, Promise<unknown>>()

const keyOf = (query: ServicingFeedQuery) => query.to ?? ''

/** Stamps each applied read, so the pricing that follows it can be told from an older one's. */
let lastReadId = 0

const messageOf = (e: any, fallback: string): string =>
  e?.response?.data?.message ??
  e?.response?.data?.error ??
  e?.message ??
  fallback

/**
 * The Servicing feed in one request: every actionable event in the window,
 * named and labelled by the backend (ADR_LOS-030), in the `ServicingParams`
 * shape the table renders. The page no longer loads 21 lists to build rows.
 *
 * Fee rows come back unpriced (`amountPending`): the amounts are a ledger
 * call, so they are read right after, in a second request, and patched in —
 * the grid paints at the statement's speed.
 *
 * The response carries the store's generation at the time of the request; if
 * a write bumped it meanwhile the reducer drops the rows and leaves the store
 * stale, and the page reloads after a delay (utils/generation.ts,
 * use-servicing-feed.ts). On failure the store keeps what it had, the user
 * is told, and the loader still stops.
 */
export const loadServicingEvents =
  (query: ServicingFeedQuery = {}) =>
  (dispatch: Dispatch, getState?: GetState) => {
    // the window the page wants now — even when the read for it is already in
    // flight, so a response for another window is dropped rather than kept
    dispatch(servicingWindowRequested(query))
    const key = keyOf(query)
    const running = inFlight.get(key)
    if (running) return running
    const current = () => getState?.().servicing
    dispatch(startLoadingServicing())
    const request = load(query, current, dispatch).finally(() => {
      // cleared BEFORE the loader stops: the page's "reload once while stale"
      // effect fires on the loader stopping, and must start a new read rather
      // than join this finished one
      inFlight.delete(key)
      dispatch(stopLoadingServicing())
    })
    inFlight.set(key, request)
    return request
  }

const load = async (
  query: ServicingFeedQuery,
  current: () => ServicingState | undefined,
  dispatch: Dispatch,
) => {
  const generation = current()?.generation ?? 0
  try {
    const response = await client.get<ServicingFeedResponse>(
      `${config.backendUrl}/api/servicing/events`,
      { params: { to: query.to } },
    )
    const readId = ++lastReadId
    // A write that landed meanwhile makes the reducer drop these rows and
    // leave the store stale; the page reloads after its delay (so PQS has
    // projected the write) — never straight away from here.
    const applied = dispatch(
      getServicing(response.data, query, generation, readId),
    )
    // the fee amounts, after the paint; never awaited by the feed. Stamped
    // with this read: the reducer drops them once another read has replaced
    // the rows they were priced for.
    void priceFees(query, readId, dispatch)
    return applied
  } catch (e: any) {
    const message = messageOf(e, 'The Servicing feed could not be loaded.')
    dispatch(servicingLoadFailed(String(message), query, generation))
    // a failure for a window the page has moved on from is nobody's news
    const state = current()
    const wanted = !state?.requested || keyOf(state.requested) === keyOf(query)
    if (wanted) {
      ErrorNotification({
        title: 'Could not load servicing',
        message: `${message} Reload the page to try again.`,
      })
    }
  }
}

/** GET /api/servicing/fee-amounts for the same window: the priced fees, by id. */
const priceFees = async (
  query: ServicingFeedQuery,
  readId: number,
  dispatch: Dispatch,
) => {
  try {
    const response = await client.get<ServicingFeeAmountsResponse>(
      `${config.backendUrl}/api/servicing/fee-amounts`,
      { params: { to: query.to } },
    )
    dispatch(setServicingFeeAmounts(response.data, readId))
  } catch (e: any) {
    dispatch(
      servicingFeeAmountsFailed(
        String(messageOf(e, 'Fee amounts could not be priced.')),
        readId,
      ),
    )
  }
}
